// Pick'em: each team picks the winner of each of the league's matchups in a
// week, for pride (scenario I6, DIRECTION A16,
// PLAN-2026-10-18-families-and-newcomers §9.1).
//
// A TEAM'S PICKS, AS ITS LINEUP IS: whoever runs the team sets them, and a
// bot picks for its own. They lock at the week's first kickoff, and only then
// does anybody see whose are whose. A pick on the winner is a point, any pick
// on a tie half a point, as Yahoo's League Pick'em scores it.

import { z } from 'zod';
import { TeamRef } from './views';

const Iso = z.string();

/** The scoring rule, one place for every server and client (`pickemPoints`). */
export const PICKEM_WIN = 1;
export const PICKEM_TIE = 0.5;

/**
 * What one pick is worth: a point on the winner of a final matchup, half a
 * point on either side of a final tie, nothing on a matchup not yet final.
 * A level score the league broke has a winner, so it is not a tie here.
 */
export function pickemPoints(
  pickTeamId: string,
  result: { final: boolean; winnerTeamId: string | null } | null,
): number {
  if (result === null || !result.final) return 0;
  if (result.winnerTeamId === null) return PICKEM_TIE;
  return result.winnerTeamId === pickTeamId ? PICKEM_WIN : 0;
}

// ── Bullseye: guess your own score (PLAN-2026-10-30-bullseye) ─────────────

/** A guess, at most: no team scores 400. */
export const GUESS_MAX = 400;

/** A guess as kept: tenths, as every score on the site is shown. */
export function guessOf(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * The week's Bullseye: the teams whose guess is nearest their own final
 * score, a tie sharing it. None while any guessing team's score is not final,
 * and none with fewer than two guesses, since one guess is nearest to nothing.
 */
export function bullseyeOf(
  guesses: readonly { teamId: string; guess: number; scored: number | null }[],
): string[] {
  if (guesses.length < 2 || guesses.some((g) => g.scored === null)) return [];
  const miss = (g: { guess: number; scored: number | null }) =>
    Math.round(Math.abs(g.guess - (g.scored ?? 0)) * 10);
  const best = Math.min(...guesses.map(miss));
  return guesses.filter((g) => miss(g) === best).map((g) => g.teamId);
}

/** One team's guess, as the week shows it once it has locked. */
export const PickemGuess = z.object({
  team: TeamRef,
  guess: z.number(),
  /** Its score once its game is final, and how far off the guess was; null before. */
  scored: z.number().nullable(),
  miss: z.number().nullable(),
  /** The week's Bullseye, or one of a tie for it. */
  bullseye: z.boolean(),
});
export type PickemGuessShape = z.infer<typeof PickemGuess>;

export const PickemMatchup = z.object({
  id: z.string(),
  kind: z.enum(['regular', 'playoff', 'consolation']),
  home: TeamRef,
  away: TeamRef,
  /** My team's pick, a team id; null when it has not picked, or I have no team. */
  myPick: z.string().nullable(),
  /**
   * Null until the matchup is final. Then its winner, or null for a tie,
   * which pays half a point on either side.
   */
  result: z.object({ winnerTeamId: z.string().nullable() }).nullable(),
  /**
   * After the week locks, the teams that picked each side, by name. Null
   * before it: nobody sees anybody else's picks until they are fixed.
   */
  picked: z.object({ home: z.array(z.string()), away: z.array(z.string()) }).nullable(),
  /**
   * The same teams as `picked`, as teams rather than names, for a screen that
   * draws each one's mark. Null before the lock, as `picked` is. Added
   * 2026-10-23.
   */
  pickedBy: z
    .object({ home: z.array(TeamRef), away: z.array(TeamRef) })
    .nullable()
    .default(null),
});
export type PickemMatchupShape = z.infer<typeof PickemMatchup>;

/** One team on the season's table. Only teams that have picked are on it. */
export const PickemRow = z.object({
  team: TeamRef,
  /** The season's points: a point a winner, half a point a tie. */
  points: z.number(),
  /** Picks on a winner, the first tiebreak after points. */
  correct: z.number().int(),
  /** Picks made on matchups that are final. */
  decided: z.number().int(),
  /**
   * How a bot team picks, in a few words ("Picks by the projections, and
   * loves an underdog"); null for a team a person runs. Added 2026-10-23.
   */
  way: z.string().nullable().default(null),
  /** Weeks it won the Bullseye, a tiebreak after correct picks. Added 2026-10-30. */
  bullseyes: z.number().int().default(0),
});

export const PickemView = z.object({
  leagueId: z.string(),
  period: z.object({ index: z.number().int(), label: z.string() }),
  /**
   * When every pick locks: the week's first kickoff, or the week's start when
   * no game is scheduled in it; null only for a week with no lineup window.
   */
  locksAt: Iso.nullable(),
  locked: z.boolean(),
  /** I run a team, the week has something to pick, and it has not locked. */
  canPick: z.boolean(),
  /** How many teams have picked this week, said before the lock without saying who. */
  teamsPicked: z.number().int(),
  /** Every week of the season with something to pick, for a stepper. */
  weeks: z.array(z.object({ index: z.number().int(), label: z.string() })),
  /** The week's matchups with two teams in them; a median game or a bye has none to pick. */
  matchups: z.array(PickemMatchup),
  /**
   * The season so far, most points first: points, then correct picks, then
   * bullseyes, then fewer decided.
   */
  table: z.array(PickemRow),
  /** My team's guess at its own score this week, or null (Bullseye). Added 2026-10-30. */
  myGuess: z.number().nullable().default(null),
  /**
   * My team's average over its last final games, up to three, a fact to guess
   * from; null for none. `myAverageGames` is how many it is over.
   */
  myAverage: z.number().nullable().default(null),
  myAverageGames: z.number().int().min(0).max(3).default(0),
  /** How many teams have guessed this week, said before the lock without saying who. */
  guessesIn: z.number().int().default(0),
  /** Every guess, nearest first once final, after the lock; null before it. */
  guesses: z.array(PickemGuess).nullable().default(null),
});
export type PickemViewShape = z.infer<typeof PickemView>;

/**
 * `PUT /api/leagues/:id/pickem/:period`: my team's picks for that week, all at
 * once, and its guess at its own score (Bullseye), a number or null to clear
 * it; either may be left out.
 */
export const SavePickemRequest = z.object({
  picks: z
    .array(z.object({ matchupId: z.string().min(1), teamId: z.string().min(1) }))
    .max(64)
    .default([]),
  guess: z
    .number()
    .min(0, 'A guess is 0 or more.')
    .max(GUESS_MAX, `A guess is ${GUESS_MAX} at most.`)
    .nullable()
    .optional(),
});
export type SavePickemRequest = z.infer<typeof SavePickemRequest>;
